import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { RichMediaState } from '@bonusly/common-action-input';
import { useHttpClient } from '@bonusly/http-client';
import { Recipient, RecipientAPIData } from '@bonusly/recipient-entities';
import { useCurrentUser } from '@bonusly/who-am-i';

type FetchRecipientsProps = {
	include_self?: boolean;
	include_non_receivable?: boolean;
	search_usergroups?: boolean;
	search_users?: boolean;
};

type FetchUseRecipientsProps = {
	query: string;
	state: RichMediaState;
};

// @TODO reuse this logic for other common (feedback be sure not include groups)
export const useFetchRecipients = (params: FetchRecipientsProps = {}) => {
	const currentUser = useCurrentUser();
	const queryClient = useQueryClient();
	const http = useHttpClient('v2');

	return useCallback(
		async ({ query, state }: FetchUseRecipientsProps): Promise<Recipient[]> => {
			const search = query.trim();
			const taggedRecipients = [...state.mentions, ...state.recipients];
			const { include_self, ...rest } = params;

			const buildRecipientsList = (data: RecipientAPIData[]) => {
				const recipients: Recipient[] = [];
				data.forEach((r) => {
					const recipient = new Recipient(r);
					if (!include_self && recipient.type === 'username' && recipient.id === currentUser.id) {
						return;
					}
					if (!include_self && recipient.type === 'group') {
						// exclude self user from groups
						const recipients = recipient.userIds.filter((id) => id !== currentUser.id);
						recipient.userIds = recipients;
						// exclude group if empty
						if (recipients.length === 0) return;
					}
					if (taggedRecipients.some((tagged) => tagged.id === recipient.id)) {
						return;
					}
					recipients.push(recipient);
				});
				return recipients;
			};

			if (!search) {
				const data = await queryClient.fetchQuery(['recipients'], () => http.get('/recipients'), { staleTime: Infinity });
				return buildRecipientsList(data.data.result as RecipientAPIData[]);
			}

			const all = { search, ...rest };
			const data = await queryClient.fetchQuery(['rich-users', all], () => {
				return http.get('/users/autocomplete', { params: all });
			});
			return buildRecipientsList(data.data.result as RecipientAPIData[]);
		},
		[currentUser, http, params, queryClient]
	);
};
